import {
  ChildProfile,
  ChildConfig,
  ChildState,
  ChildState as _CS,
  GoldTransaction,
  PendingApproval,
  RewardRequest,
  ProductMetadata,
  ParentNotification,
  SAMPLE_APPS,
  DEFAULT_OFF_PHONE_EARNERS,
  DAYS_OF_WEEK,
  MAX_DAILY_TASK_GOLD,
} from "./types";
import { buildRewardPreview } from "./rewardPreview";
import { syncProfiles, syncConfig, syncState, syncDeleteChild, getDeviceId } from "./supabaseSync";
import { readLocalJson } from "./safeJson";

const PROFILES_KEY = "screentime-profiles";
const CONFIGS_KEY = "screentime-configs";
const STATES_KEY = "screentime-states";
const PIN_KEY = "screentime-pin";

// ── PIN ──
export function getPin(): string {
  return localStorage.getItem(PIN_KEY) || "";
}
export function setPin(pin: string) {
  localStorage.setItem(PIN_KEY, pin);
  syncProfiles(getProfiles(), pin);
}
export function hasPin(): boolean {
  return !!localStorage.getItem(PIN_KEY);
}

// ── Profiles ──
export function getProfiles(): ChildProfile[] {
  return readLocalJson<ChildProfile[]>(PROFILES_KEY, []);
}

export function saveProfiles(profiles: ChildProfile[]) {
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
  syncProfiles(profiles, getPin());
}

/**
 * Seeds the default ChildConfig + ChildState for a child. Extracted from
 * the body of addProfile() so the onboarding flow (which creates the
 * `children` DB row first and needs to mirror the resulting DB UUID into
 * localStorage) can call this directly with the DB UUID — preserving every
 * default (SAMPLE_APPS, off-phone earners, marketplace categories, gold
 * balance) that ParentPortal / Hub depend on for any UI to populate.
 *
 * Without this seed the /parent Control Room shows empty dropdowns and the
 * pro / junior playgrounds have no apps to choose from.
 */
export function seedDefaultChildData(childId: string): void {
  const config: ChildConfig = {
    childId,
    apps: [...SAMPLE_APPS],
    ratio: { earnMinutes: 1, playMinutes: 2 },
    daySchedules: Object.fromEntries(
      DAYS_OF_WEEK.map((day) => [day, { earnMinutes: 1, playMinutes: 2 }])
    ),
    offPhoneEarners: [...DEFAULT_OFF_PHONE_EARNERS],
    screenTimeLimitMinutes: 60,
    rewardItems: [],
    goldPerMinuteTrade: 2,
    goldPerExchange: 1,
    marketplace: {
      enabledCategories: ["food", "entertainment", "sports", "shopping", "creative", "experiences", "education", "gaming"],
      disabledRewardIds: [],
    },
  };
  saveConfig(config);

  const state: ChildState = {
    childId,
    earnedPlayMinutes: 0,
    usedPlayMinutes: 0,
    totalScreenTimeToday: 0,
    sessions: [],
    gold: 500,
    goldTransactions: [],
    streaks: { daily: { lastDate: "", count: 0 }, perActivity: {} },
    purchasedRewards: [],
    pendingApprovals: [],
  };
  saveState(state);
}

export function addProfile(name: string, avatar: string, ageGroup: ChildProfile["ageGroup"]): ChildProfile {
  const profile: ChildProfile = {
    id: crypto.randomUUID(),
    name,
    avatar,
    ageGroup,
    isPaused: false,
  };
  const profiles = getProfiles();
  profiles.push(profile);
  saveProfiles(profiles);

  seedDefaultChildData(profile.id);
  return profile;
}

export async function updateProfile(id: string, updates: Partial<Pick<ChildProfile, "name" | "avatar" | "ageGroup">>): Promise<void> {
  const profiles = getProfiles();
  const profile = profiles.find((p) => p.id === id);
  if (profile) {
    Object.assign(profile, updates);
    localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
    await syncProfiles(profiles, getPin());
  }
}

export function removeProfile(id: string) {
  saveProfiles(getProfiles().filter((p) => p.id !== id));
  const configs = getAllConfigs();
  delete configs[id];
  localStorage.setItem(CONFIGS_KEY, JSON.stringify(configs));
  const states = getAllStates();
  delete states[id];
  localStorage.setItem(STATES_KEY, JSON.stringify(states));
  syncDeleteChild(id);
}

export function togglePause(id: string) {
  const profiles = getProfiles();
  const profile = profiles.find((p) => p.id === id);
  if (profile) {
    profile.isPaused = !profile.isPaused;
    saveProfiles(profiles);
  }
}

// ── Configs ──
function getAllConfigs(): Record<string, ChildConfig> {
  return readLocalJson<Record<string, ChildConfig>>(CONFIGS_KEY, {});
}

export function getConfig(childId: string): ChildConfig | null {
  return getAllConfigs()[childId] || null;
}

export function saveConfig(config: ChildConfig) {
  const all = getAllConfigs();
  all[config.childId] = config;
  localStorage.setItem(CONFIGS_KEY, JSON.stringify(all));
  syncConfig(config.childId, config);
}

// ── States ──
function getAllStates(): Record<string, ChildState> {
  return readLocalJson<Record<string, ChildState>>(STATES_KEY, {});
}

export function getState(childId: string): ChildState | null {
  return getAllStates()[childId] || null;
}

export function saveState(state: ChildState) {
  const all = getAllStates();
  (state as any)._localUpdatedAt = Date.now();
  all[state.childId] = state;
  localStorage.setItem(STATES_KEY, JSON.stringify(all));
  syncState(state.childId, state);
}

export function addEarnedTime(childId: string, source: string, earnedMinutes: number, ratio: { earnMinutes: number; playMinutes: number }) {
  const state = getState(childId);
  if (!state) return;
  const playMinutesGranted = Math.floor((earnedMinutes / ratio.earnMinutes) * ratio.playMinutes);
  state.earnedPlayMinutes += playMinutesGranted;
  state.sessions.push({
    childId,
    source,
    earnedMinutes,
    playMinutesGranted,
    timestamp: new Date().toISOString(),
  });
  saveState(state);
}

export function usePlayTime(childId: string, minutes: number) {
  const state = getState(childId);
  if (!state) return;
  state.usedPlayMinutes += minutes;
  saveState(state);
}

// Helper to ensure old states have gold fields (migration)
function ensureGoldFields(state: ChildState): ChildState {
  if (state.gold === undefined) state.gold = 0;
  if (!state.goldTransactions) state.goldTransactions = [];
  if (!state.streaks) state.streaks = { daily: { lastDate: "", count: 0 }, perActivity: {} };
  if (!state.purchasedRewards) state.purchasedRewards = [];
  if (!state.pendingApprovals) state.pendingApprovals = [];
  if (!state.rewardRequests) state.rewardRequests = [];
  if (!state.parentNotifications) state.parentNotifications = [];
  return state;
}

export function getStateWithGold(childId: string): ChildState | null {
  const state = getState(childId);
  if (!state) return null;
  return ensureGoldFields(state);
}

export function addGold(childId: string, amount: number, type: GoldTransaction["type"], description: string) {
  const state = getStateWithGold(childId);
  if (!state) return;
  state.gold += amount;
  state.goldTransactions.push({ type, amount, description, timestamp: new Date().toISOString() });
  saveState(state);
}

export function spendGold(childId: string, amount: number, description: string, addToWallet: boolean = true): boolean {
  const state = getStateWithGold(childId);
  if (!state || state.gold < amount) return false;
  state.gold -= amount;
  state.goldTransactions.push({ type: "purchase", amount: -amount, description, timestamp: new Date().toISOString() });
  if (addToWallet) state.purchasedRewards.push(description);
  // Only create parent notification for actual reward purchases, not crew contributions
  if (addToWallet) {
    const profile = getProfiles().find(p => p.id === childId);
    if (!state.parentNotifications) state.parentNotifications = [];
    state.parentNotifications.push({
      id: crypto.randomUUID(),
      type: "reward_unlocked",
      childName: profile?.name || "Child",
      rewardName: description,
      goldSpent: amount,
      timestamp: new Date().toISOString(),
    });
  }
  saveState(state);
  return true;
}

export function tradeMinutesForGold(childId: string, minutes: number, rate: number): boolean {
  const state = getStateWithGold(childId);
  if (!state) return false;
  const available = state.earnedPlayMinutes - state.usedPlayMinutes;
  if (available < minutes) return false;
  const goldEarned = Math.floor(minutes * rate);
  state.usedPlayMinutes += minutes;
  state.gold += goldEarned;
  state.goldTransactions.push({ type: "trade", amount: goldEarned, description: `Traded ${minutes} min for ${goldEarned} gold`, timestamp: new Date().toISOString() });
  saveState(state);
  return true;
}

export function tradeGoldForMinutes(childId: string, gold: number, rate: number): boolean {
  const state = getStateWithGold(childId);
  if (!state || state.gold < gold) return false;
  const minutesEarned = Math.floor(gold / rate);
  state.gold -= gold;
  state.usedPlayMinutes -= minutesEarned;
  state.goldTransactions.push({ type: "trade", amount: -gold, description: `Traded ${gold} gold for ${minutesEarned} min`, timestamp: new Date().toISOString() });
  saveState(state);
  return true;
}

export function updateStreaks(childId: string, activityName?: string) {
  const state = getStateWithGold(childId);
  if (!state) return;
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  // Daily streak
  if (state.streaks.daily.lastDate !== today) {
    if (state.streaks.daily.lastDate === yesterday) {
      state.streaks.daily.count += 1;
    } else {
      state.streaks.daily.count = 1;
    }
    state.streaks.daily.lastDate = today;
  }

  // Per-activity streak
  if (activityName) {
    const actStreak = state.streaks.perActivity[activityName] || { lastDate: "", count: 0 };
    if (actStreak.lastDate !== today) {
      if (actStreak.lastDate === yesterday) {
        actStreak.count += 1;
      } else {
        actStreak.count = 1;
      }
      actStreak.lastDate = today;
      // Bonus gold for activity streaks (2 gold per streak day)
      if (actStreak.count > 1) {
        const bonus = Math.min(actStreak.count, 10);
        state.gold += bonus;
        state.goldTransactions.push({ type: "bonus_activity", amount: bonus, description: `${activityName} streak x${actStreak.count}`, timestamp: new Date().toISOString() });
      }
      state.streaks.perActivity[activityName] = actStreak;
    }
  }

  saveState(state);
}

// ── Daily Task Gold Tracking ──
export function getDailyTaskGoldEarned(childId: string): number {
  const state = getStateWithGold(childId);
  if (!state) return 0;
  const today = new Date().toISOString().slice(0, 10);
  return state.goldTransactions
    .filter(t => t.type === "bonus_activity" && t.timestamp.startsWith(today) && t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);
}

// ── Pending Approvals ──
export function submitForApproval(childId: string, earnerName: string, earnerIcon: string, goldAmount: number) {
  const state = getStateWithGold(childId);
  if (!state) return;
  const approval: PendingApproval = {
    id: crypto.randomUUID(),
    earnerName,
    earnerIcon,
    goldAmount,
    timestamp: new Date().toISOString(),
    status: "pending",
  };
  state.pendingApprovals.push(approval);
  // Also log a session so the child sees it as "pending" today
  state.sessions.push({
    childId,
    source: earnerName,
    earnedMinutes: 0,
    playMinutesGranted: 0,
    timestamp: new Date().toISOString(),
  });
  saveState(state);
}

export function approveTask(childId: string, approvalId: string): { success: boolean; capped?: boolean } {
  const state = getStateWithGold(childId);
  if (!state) return { success: false };
  const approval = state.pendingApprovals.find(a => a.id === approvalId);
  if (!approval || approval.status !== "pending") return { success: false };
  
  // Enforce daily cap
  const alreadyEarned = getDailyTaskGoldEarned(childId);
  const remaining = Math.max(0, MAX_DAILY_TASK_GOLD - alreadyEarned);
  const actualGold = Math.min(approval.goldAmount, remaining);
  
  approval.status = "approved";
  if (actualGold > 0) {
    state.gold += actualGold;
    state.goldTransactions.push({
      type: "bonus_activity",
      amount: actualGold,
      description: `${approval.earnerName}: +${actualGold} gold (approved${actualGold < approval.goldAmount ? ", daily cap reached" : ""})`,
      timestamp: new Date().toISOString(),
    });
  }

  // Create/update session so chores appear in Insights
  const config = getConfig(childId);
  const earner = config?.offPhoneEarners.find(e => e.name === approval.earnerName);
  const earnedMinutes = earner?.minutesPerUnit || 0;
  const ratio = config?.ratio || { earnMinutes: 1, playMinutes: 1 };
  const playMinutesGranted = earnedMinutes > 0 ? Math.floor((earnedMinutes / ratio.earnMinutes) * ratio.playMinutes) : 0;

  // Update the placeholder session (earnedMinutes was 0) or add a new one
  const todayStr = new Date().toISOString().slice(0, 10);
  const pendingSession = state.sessions.find(
    s => s.source === approval.earnerName && s.timestamp.startsWith(todayStr) && s.earnedMinutes === 0
  );
  if (pendingSession) {
    pendingSession.earnedMinutes = earnedMinutes;
    pendingSession.playMinutesGranted = playMinutesGranted;
    pendingSession.timestamp = new Date().toISOString();
  } else {
    state.sessions.push({
      childId,
      source: approval.earnerName,
      earnedMinutes,
      playMinutesGranted,
      timestamp: new Date().toISOString(),
    });
  }

  if (playMinutesGranted > 0) {
    state.earnedPlayMinutes += playMinutesGranted;
  }

  saveState(state);
  return { success: true, capped: actualGold < approval.goldAmount };
}

export function rejectTask(childId: string, approvalId: string) {
  const state = getStateWithGold(childId);
  if (!state) return;
  const approval = state.pendingApprovals.find(a => a.id === approvalId);
  if (!approval || approval.status !== "pending") return;
  approval.status = "rejected";
  // Remove the session so child can retry
  const todayStr = new Date().toISOString().slice(0, 10);
  state.sessions = state.sessions.filter(
    s => !(s.source === approval.earnerName && s.timestamp.startsWith(todayStr) && s.earnedMinutes === 0)
  );
  saveState(state);
}

export function markInboxSeen(childId: string, seenAt = new Date().toISOString()) {
  const state = getStateWithGold(childId);
  if (!state) return null;
  state.lastInboxSeenAt = seenAt;
  saveState(state);
  return seenAt;
}

export function markPendingApprovalPopupSeen(childId: string, approvalId: string) {
  const state = getStateWithGold(childId);
  if (!state) return;
  const approval = state.pendingApprovals.find(a => a.id === approvalId);
  if (!approval || approval.childPopupSeenAt) return;
  approval.childPopupSeenAt = new Date().toISOString();
  saveState(state);
}

// ── Reward Requests ──
export function submitRewardRequest(
  childId: string, name: string, description: string, icon: string,
  crewMemberIds?: string[],
  invitedFriend?: { id: string; name: string; avatar: string },
  itemLink?: string,
  linkPreview?: { title?: string; image?: string; price?: string; url: string }
) {
  const state = getStateWithGold(childId);
  if (!state) return;
  const isCrewRequest = !!crewMemberIds && crewMemberIds.length > 1;
  const isSharedRequest = !!invitedFriend;

  const rewardPreview = buildRewardPreview(linkPreview, itemLink);

  // Build product metadata from link preview
  let productMeta: ProductMetadata | undefined;
  if (linkPreview) {
    let domain: string | undefined;
    try { domain = new URL(linkPreview.url).hostname.replace('www.', ''); } catch { /* skip */ }

    let priceValue: number | undefined;
    let currency: string | undefined;
    let suggestedCoins: number | undefined;

    if (linkPreview.price) {
      const priceStr = linkPreview.price.trim();
      if (priceStr.startsWith('£')) { currency = 'GBP'; }
      else if (priceStr.startsWith('$')) { currency = 'USD'; }
      else if (priceStr.startsWith('€')) { currency = 'EUR'; }
      else { currency = 'GBP'; }
      const numMatch = priceStr.match(/[\d,.]+/);
      if (numMatch) {
        priceValue = parseFloat(numMatch[0].replace(/,/g, ''));
        if (!isNaN(priceValue) && currency === 'GBP') {
          suggestedCoins = Math.round(priceValue * 10);
        }
      }
    }

    productMeta = {
      productTitle: linkPreview.title,
      productUrl: linkPreview.url,
      productDomain: domain,
      productImage: linkPreview.image,
      productPriceValue: priceValue,
      productCurrency: currency,
      suggestedCoinValue: suggestedCoins,
      priceDetectedAt: new Date().toISOString(),
    };
  }

  const request: RewardRequest = {
    id: crypto.randomUUID(),
    childId,
    name,
    description: description || undefined,
    icon,
    itemLink: itemLink || undefined,
    linkPreview: linkPreview || undefined,
    rewardPreview,
    productMeta,
    status: "pending",
    timestamp: new Date().toISOString(),
    isCrewRequest: isSharedRequest || isCrewRequest,
    crewMemberIds: isSharedRequest ? [childId, invitedFriend.id] : (isCrewRequest ? crewMemberIds : undefined),
    sharedStatus: isSharedRequest ? "waiting" : undefined,
    invitedFriendId: invitedFriend?.id,
    invitedFriendName: invitedFriend?.name,
    invitedFriendAvatar: invitedFriend?.avatar,
    inviteTimestamp: isSharedRequest ? new Date().toISOString() : undefined,
  };

  console.log('[RewardRequest] submitting reward request:', {
    childId,
    linkPreview,
    rewardPreview,
    request,
  });
  if (!state.rewardRequests) state.rewardRequests = [];
  state.rewardRequests.push(request);
  saveState(state);

  // For shared requests, add a friend invite notification to the friend's state
  if (isSharedRequest) {
    const friendState = getStateWithGold(invitedFriend.id);
    if (friendState) {
      if (!friendState.rewardRequests) friendState.rewardRequests = [];
      // Add the request to friend's state too (they'll see it as an invite)
      friendState.rewardRequests.push({ ...request, childId: invitedFriend.id });
      saveState(friendState);
    }
  }

  // Also add the request to each crew member's state so they can see it (non-shared legacy)
  if (!isSharedRequest && isCrewRequest && crewMemberIds) {
    for (const memberId of crewMemberIds) {
      if (memberId === childId) continue;
      const memberState = getStateWithGold(memberId);
      if (!memberState) continue;
      if (!memberState.rewardRequests) memberState.rewardRequests = [];
      memberState.rewardRequests.push({ ...request, childId: memberId });
      saveState(memberState);
    }
  }
}

/** Friend joins a shared reward request */
export function joinSharedRequest(friendChildId: string, requestId: string) {
  // Update the friend's copy
  const friendState = getStateWithGold(friendChildId);
  if (!friendState) return;
  const friendReq = friendState.rewardRequests?.find(r => r.id === requestId);
  if (friendReq) {
    friendReq.sharedStatus = "active";
    saveState(friendState);
  }

  // Find the original requester and update their copy
  const profiles = getProfiles();
  for (const p of profiles) {
    if (p.id === friendChildId) continue;
    const state = getStateWithGold(p.id);
    if (!state?.rewardRequests) continue;
    const req = state.rewardRequests.find(r => r.id === requestId);
    if (req) {
      req.sharedStatus = "active";
      saveState(state);
      break;
    }
  }
}

/** Friend declines a shared reward request - convert to solo */
export function declineSharedRequest(friendChildId: string, requestId: string) {
  // Update the friend's copy - remove it
  const friendState = getStateWithGold(friendChildId);
  if (friendState?.rewardRequests) {
    friendState.rewardRequests = friendState.rewardRequests.filter(r => r.id !== requestId);
    saveState(friendState);
  }

  // Find the original requester and convert to solo
  const profiles = getProfiles();
  for (const p of profiles) {
    if (p.id === friendChildId) continue;
    const state = getStateWithGold(p.id);
    if (!state?.rewardRequests) continue;
    const req = state.rewardRequests.find(r => r.id === requestId);
    if (req && req.sharedStatus === "waiting") {
      req.sharedStatus = "solo_converted";
      req.isCrewRequest = false;
      req.crewMemberIds = undefined;
      req.friendDeclined = true;
      saveState(state);
      break;
    }
  }
}

/** Auto-convert expired shared requests (48h timeout) */
export function autoConvertExpiredSharedRequests() {
  const profiles = getProfiles();
  const now = Date.now();
  const TIMEOUT_MS = 48 * 60 * 60 * 1000; // 48 hours

  for (const p of profiles) {
    const state = getStateWithGold(p.id);
    if (!state?.rewardRequests) continue;
    let changed = false;
    for (const req of state.rewardRequests) {
      if (req.sharedStatus === "waiting" && req.inviteTimestamp) {
        const elapsed = now - new Date(req.inviteTimestamp).getTime();
        if (elapsed >= TIMEOUT_MS) {
          req.sharedStatus = "solo_converted";
          req.isCrewRequest = false;
          req.crewMemberIds = undefined;
          changed = true;

          // Remove from friend's state
          if (req.invitedFriendId) {
            const friendState = getStateWithGold(req.invitedFriendId);
            if (friendState?.rewardRequests) {
              friendState.rewardRequests = friendState.rewardRequests.filter(r => r.id !== req.id);
              saveState(friendState);
            }
          }
        }
      }
    }
    if (changed) saveState(state);
  }
}

export function approveRewardRequest(childId: string, requestId: string, goldCost: number) {
  const state = getStateWithGold(childId);
  if (!state) return;
  const req = state.rewardRequests?.find(r => r.id === requestId);
  if (!req || req.status !== "pending") return;
  req.status = "approved";
  req.goldCost = goldCost;
  if (req.isCrewRequest) req.crewLocked = true;

  // Log activity entry
  if (!state.parentNotifications) state.parentNotifications = [];
  state.parentNotifications.push({
    id: crypto.randomUUID(),
    type: "reward_approved",
    childName: getProfiles().find(p => p.id === childId)?.name || "Child",
    rewardName: req.name,
    goldSpent: goldCost,
    timestamp: new Date().toISOString(),
    seen: true,
  });
  saveState(state);

  // Sync approval to all crew members
  if (req.isCrewRequest && req.crewMemberIds) {
    for (const memberId of req.crewMemberIds) {
      if (memberId === childId) continue;
      const memberState = getStateWithGold(memberId);
      if (!memberState) continue;
      const memberReq = memberState.rewardRequests?.find(r => r.id === requestId);
      if (memberReq) {
        memberReq.status = "approved";
        memberReq.goldCost = goldCost;
        memberReq.crewLocked = true;
        saveState(memberState);
      }
    }
  }
}

export function denyRewardRequest(childId: string, requestId: string) {
  const state = getStateWithGold(childId);
  if (!state) return;
  const req = state.rewardRequests?.find(r => r.id === requestId);
  if (!req || req.status !== "pending") return;
  req.status = "denied";

  // Log activity entry
  if (!state.parentNotifications) state.parentNotifications = [];
  state.parentNotifications.push({
    id: crypto.randomUUID(),
    type: "reward_denied",
    childName: getProfiles().find(p => p.id === childId)?.name || "Child",
    rewardName: req.name,
    goldSpent: 0,
    timestamp: new Date().toISOString(),
    seen: true,
  });
  saveState(state);

  // Sync denial to all crew members
  if (req.isCrewRequest && req.crewMemberIds) {
    for (const memberId of req.crewMemberIds) {
      if (memberId === childId) continue;
      const memberState = getStateWithGold(memberId);
      if (!memberState) continue;
      const memberReq = memberState.rewardRequests?.find(r => r.id === requestId);
      if (memberReq) {
        memberReq.status = "denied";
        saveState(memberState);
      }
    }
  }
}

export function contributeToRewardRequest(childId: string, requestId: string, amount: number): boolean {
  const state = getStateWithGold(childId);
  if (!state) return false;
  const req = state.rewardRequests?.find(r => r.id === requestId);
  if (!req || req.status !== "approved" || !req.goldCost) return false;

  const alreadyContributed = req.contributedGold || 0;
  const remaining = req.goldCost - alreadyContributed;
  if (remaining <= 0) return false; // already fully funded

  const actualAmount = Math.min(amount, remaining, state.gold);
  if (actualAmount <= 0) return false;

  state.gold -= actualAmount;
  req.contributedGold = alreadyContributed + actualAmount;
  state.goldTransactions.push({
    type: "purchase",
    amount: -actualAmount,
    description: `Contributed to: ${req.name}`,
    timestamp: new Date().toISOString(),
  });

  // Sync to crew members
  if (req.isCrewRequest && req.crewMemberIds) {
    for (const memberId of req.crewMemberIds) {
      if (memberId === childId) continue;
      const memberState = getStateWithGold(memberId);
      if (!memberState) continue;
      const memberReq = memberState.rewardRequests?.find(r => r.id === requestId);
      if (memberReq) {
        // Update the specific member's contribution view (they see overall progress)
        saveState(memberState);
      }
    }
  }

  saveState(state);
  return true;
}

export function redeemRewardRequest(childId: string, requestId: string): boolean {
  const state = getStateWithGold(childId);
  if (!state) return false;
  const req = state.rewardRequests?.find(r => r.id === requestId);
  if (!req || req.status !== "approved" || !req.goldCost) return false;

  // For individual requests, check contribution is complete
  const contributed = req.contributedGold || 0;
  if (contributed < req.goldCost) return false;

  // Transition to ready_to_claim (parent must confirm delivery)
  req.status = "ready_to_claim";

  // Add parent notification
  if (!state.parentNotifications) state.parentNotifications = [];
  state.parentNotifications.push({
    id: crypto.randomUUID(),
    type: "reward_unlocked",
    childName: getProfiles().find(p => p.id === childId)?.name || "Child",
    rewardName: req.name,
    goldSpent: req.goldCost,
    timestamp: new Date().toISOString(),
  });

  saveState(state);
  return true;
}

/** Parent confirms reward has been delivered */
export function markRewardDelivered(childId: string, requestId: string): boolean {
  const state = getStateWithGold(childId);
  if (!state) return false;
  const req = state.rewardRequests?.find(r => r.id === requestId);
  if (!req || req.status !== "ready_to_claim") return false;

  req.status = "claimed";
  req.claimedAt = new Date().toISOString();
  state.purchasedRewards.push(`Reward Request: ${req.name}`);
  saveState(state);

  // Sync to crew members
  if (req.isCrewRequest && req.crewMemberIds) {
    for (const memberId of req.crewMemberIds) {
      if (memberId === childId) continue;
      const memberState = getStateWithGold(memberId);
      if (!memberState) continue;
      const memberReq = memberState.rewardRequests?.find(r => r.id === requestId);
      if (memberReq) {
        memberReq.status = "claimed";
        memberReq.claimedAt = req.claimedAt;
        saveState(memberState);
      }
    }
  }

  return true;
}

/** Cancel a pending reward request */
export function cancelRewardRequest(childId: string, requestId: string): boolean {
  const state = getStateWithGold(childId);
  if (!state) return false;
  const idx = state.rewardRequests?.findIndex(r => r.id === requestId && r.status === "pending");
  if (idx === undefined || idx < 0) return false;
  state.rewardRequests!.splice(idx, 1);
  saveState(state);
  return true;
}

/** Remove gold from an active reward request (refund) */
export function removeGoldFromRewardRequest(childId: string, requestId: string, amount: number): boolean {
  const state = getStateWithGold(childId);
  if (!state) return false;
  const req = state.rewardRequests?.find(r => r.id === requestId);
  if (!req || req.status !== "approved" || !req.goldCost) return false;

  const contributed = req.contributedGold || 0;
  const actualAmount = Math.min(amount, contributed);
  if (actualAmount <= 0) return false;

  state.gold += actualAmount;
  req.contributedGold = contributed - actualAmount;
  state.goldTransactions.push({
    type: "trade",
    amount: actualAmount,
    description: `Refunded from: ${req.name}`,
    timestamp: new Date().toISOString(),
  });

  saveState(state);
  return true;
}

/**
 * Parent cancels an approved (active) reward goal.
 * Refunds each contributor (including all crew members) their contributed gold,
 * notifies them via parent inbox, and removes the request from each member's state.
 * Returns total amount refunded across all contributors.
 */
export function cancelApprovedRewardRequest(childId: string, requestId: string): { refunded: number } {
  const state = getStateWithGold(childId);
  if (!state) return { refunded: 0 };
  const req = state.rewardRequests?.find(r => r.id === requestId);
  if (!req) return { refunded: 0 };
  if (req.status !== "approved" && req.status !== "ready_to_claim") return { refunded: 0 };

  const goalName = req.name;
  let totalRefunded = 0;

  const refundOne = (memberId: string) => {
    const ms = getStateWithGold(memberId);
    if (!ms) return;
    const mreq = ms.rewardRequests?.find(r => r.id === requestId);
    if (!mreq) return;
    const refund = mreq.contributedGold || 0;
    if (refund > 0) {
      ms.gold += refund;
      ms.goldTransactions.push({
        type: "trade",
        amount: refund,
        description: `Refund: '${goalName}' goal cancelled`,
        timestamp: new Date().toISOString(),
      });
      totalRefunded += refund;
    }
    if (!ms.parentNotifications) ms.parentNotifications = [];
    ms.parentNotifications.push({
      id: crypto.randomUUID(),
      type: "reward_denied",
      childName: getProfiles().find(p => p.id === memberId)?.name || "Child",
      rewardName: refund > 0
        ? `'${goalName}' was cancelled — ${refund} gold refunded`
        : `'${goalName}' was cancelled`,
      goldSpent: 0,
      timestamp: new Date().toISOString(),
    });
    ms.rewardRequests = ms.rewardRequests?.filter(r => r.id !== requestId) || [];
    saveState(ms);
  };

  // Refund the primary child + any crew members
  const memberIds = new Set<string>([childId]);
  if (req.isCrewRequest && req.crewMemberIds) {
    req.crewMemberIds.forEach(id => memberIds.add(id));
  }
  memberIds.forEach(refundOne);

  return { refunded: totalRefunded };
}
